/** PostHog feature flag: Submit ITEL flow (roof material question on Review and approve photos, Submit/Receive ITEL tasks) */
export const SUBMIT_ITEL_FEATURE_FLAG = "submit-itel";

/** PostHog feature flag: show the insurance view — top-level nav item + insurance Views sub-items (off hides them) */
export const INSURANCE_VIEW_FEATURE_FLAG = "insurance-view-enabled";

/** PostHog feature flag: customer appeasements workflow */
export const CUSTOMER_APPEASEMENTS_FEATURE_FLAG = "customer-appeasements";

/** PostHog feature flag: homeowner status timeline on partner flows without a registered timeline */
export const GENERAL_HOMEOWNER_STATUS_TIMELINE_FLAG = "general-homeowner-status-timeline";

/** PostHog feature flag: site-visit photo annotation (notes + tags on bid site-visit photos). */
export const SITE_VISIT_PHOTO_ANNOTATION_FEATURE_FLAG = "site-visit-photo-annotation";

/**
 * PostHog feature flag: gate + configure vendor-credit auto-apply. Carries a JSON payload
 * `{ mode: "OFF" | "LOG_ONLY" | "LIVE", cutoverAt?: ISO date }`. Read fail-closed (off/absent → OFF).
 */
export const VENDOR_CREDIT_AUTO_APPLY_FEATURE_FLAG = "vendor-credit-auto-apply";

/** PostHog feature flag: quoting sandbox tool (admin nav link, list + editor pages, sandboxQuoting procedures). Targets internal users by email. */
export const QUOTING_SANDBOX_FEATURE_FLAG = "quoting-sandbox";

/** PostHog feature flag: hourly sweep that marks a Nomad visit MISSED once its block ends unstarted and auto-schedules it again. */
export const NOMAD_MISSED_VISIT_RESCHEDULE_FEATURE_FLAG = "nomad-missed-visit-reschedule";

/** PostHog feature flag: account-wide kill-switch for the Inspectify integration client (create-order + read endpoints) */
export const INSPECTIFY_FEATURE_FLAG = "inspectify-feature-flag";

export const CONTRACTOR_BLOCKING_CARDS_FEATURE_FLAG = "contractor-blocking-cards";

/** PostHog flag gating specialist-line resolution for in-window automated homeowner SMS. Property-filtered
 * on partnerFlow, not person-keyed — the send path runs as REMI_AUTOMATIONS, so a person-keyed flag would
 * evaluate against the bot. */
export const SPECIALIST_WINDOW_SENDER_FEATURE_FLAG = "specialist-window-sender";

/** PostHog feature flag: live-call widget (connect unknown callers to a bid) — limited rollout */
export const LIVE_CALL_WIDGET_FEATURE_FLAG = "live-call-widget";

/** PostHog feature flag: My daily actions page + nav entry + home redirect */
export const MY_DAILY_ACTIONS_FEATURE_FLAG = "my-daily-actions";

/** PostHog feature flag: Eden nav entry — pilot rollout. Key stays `agent-cockpit`: a new key would drop enrolled pilots. */
export const AGENT_EDEN_FEATURE_FLAG = "agent-cockpit";

/** Dark-deploy gate: flip to honor a STOP to ANY Remi line as point-of-contact
 *  intro suppression, since a fresh AM line can never match a homeowner's prior STOP to another line.
 *  Typed `boolean` (not literal `false`) so `if (CROSS_LINE_STOP_HONORED)` is not a dead branch. */
export const CROSS_LINE_STOP_HONORED: boolean = false;

export const MANUAL_DATES_LOCKDOWN_FEATURE_FLAG = "manual-dates-lockdown";

/** PostHog feature flag: Openly quote PDF tear-out/replace layout. Keep it a global on/off — the stored
 * PDF renders as REMI_AUTOMATIONS while the browser preview renders as the logged-in user, so a
 * percentage or cohort rollout evaluates two identities and desyncs the two renders. */
export const OPENLY_TEAR_OUT_REPLACE_FEATURE_FLAG = "openly-tear-out-replace";

/** PostHog feature flag: the redesigned work order PDF for full and partial roof work orders. Keep it a global
 * on/off — the stored PDF builds as REMI_AUTOMATIONS while the browser preview builds as the logged-in user,
 * so a percentage or cohort rollout evaluates two identities and desyncs the two renders. */
export const WORK_ORDER_ROOF_PDF_REDESIGN_FEATURE_FLAG = "work-order-roof-pdf-redesign";

export const MFA_ENFORCEMENT_FEATURE_FLAG = "mfa-enforcement";

export const MFA_ENROLLMENT_PROMPT_FEATURE_FLAG = "mfa-enrollment-prompt";

/** PostHog feature flag: file Sunrun redesign SLA-breach tickets (NSYNC-7607). Keep it a global on/off —
 * the sweep runs as REMI_AUTOMATIONS, so a percentage rollout evaluates one identity for every project. */
export const SUNRUN_REDESIGN_SLA_TICKETS_FEATURE_FLAG = "sunrun-redesign-sla-tickets";

/** PostHog feature flag: gate XA estimate mismatch automated ticket creation (Westhill scraper → oddjob-api).
 * Keep it a global on/off — the caller is REMI_AUTOMATIONS, so a person-keyed flag evaluates one identity for every project. */
export const XA_ESTIMATE_MISMATCH_TICKETS_FEATURE_FLAG = "xa-estimate-mismatch-tickets";

/** PostHog feature flag: let an XA assignment's header total stand in for a missing correction row in the
 * estimate mismatch check. Keep it a global on/off — the caller is REMI_AUTOMATIONS. */
export const XA_ASSIGNMENT_TOTAL_MISMATCH_TICKETS_FEATURE_FLAG = "xa-assignment-total-mismatch-tickets";

/** PostHog feature flag: kill switch for the cron that reconciles Remi resolved with OpenPhone Done.
 * Keep it a global on/off: the cron runs as REMI_AUTOMATIONS, so a percentage/cohort rollout evaluates
 * one identity for every line. Enabled = cron runs; disable to kill. */
export const RECONCILE_CONVERSATIONS_WITH_OPEN_PHONE_FEATURE_FLAG = "reconcile-conversations-with-open-phone";

/** Keep it a global on/off — the claim path runs as REMI_AUTOMATIONS, so a percentage rollout
 * evaluates one identity for every financing. */
export const CLAIM_FINANCING_FEATURE_FLAG = "claim-financing";

/** PostHog feature flag: the TaskSummarySection AI-generated summary subsection on the internal task
 * details page (AM pilot). Flag off renders nothing. */
export const TASK_SUMMARY_FEATURE_FLAG = "task-summary";
/** PostHog feature flag: gate the history-backed project activity feed tab during rollout. Person-keyed
 * on purpose — the flag is read in the browser by the viewing user, so a percentage rollout gives a
 * stable per-person cohort rather than one identity for everyone. */
export const PROJECT_HISTORY_FEED_FEATURE_FLAG = "project-history-feed";

/** PostHog feature flag: the daily job that creates QA visits for the top QA recommendations. Keep it a
 * global on/off — the job runs as REMI_AUTOMATIONS, so a percentage rollout hashes one bot identity. Off,
 * recommendations are only acted on by hand from the dispatch board's QA recommendations drawer. */
export const QA_VISIT_AUTO_CREATION_FEATURE_FLAG = "qa-visit-auto-creation";

/** PostHog feature flag: the dispatch board's live jobs drawer, which creates LIVE_VISIT projects and texts their
 * homeowners LIVE_VISIT_WALKTHROUGH_NOTICE_HOMEOWNER. Stays off for 12h after the deploy adding both: Project.category
 * reads parse strictly, so an instance still on the old build throws on any project carrying it. Global on/off. */
export const LIVE_VISIT_CREATION_FEATURE_FLAG = "live-visit-creation";

/** PostHog feature flag: under `qa-visit-auto-creation`, create one geographic cluster of QA visits per service area
 * each run and text each homeowner for an unattended walkthrough. Stays off for 12h after the deploy adding
 * QA_WALKTHROUGH_REQUEST_HOMEOWNER, which an instance on the old build cannot read. Global on/off. */
export const QA_VISIT_WALKTHROUGH_TEXTS_FEATURE_FLAG = "qa-visit-walkthrough-texts";

/** PostHog feature flag: render the QA report onto the corrections an ops decision links. Stays off for 12h
 * after the deploy adding FileTag.QA_REPORT: FileToFileTag reads parse tagId strictly, so an instance still on
 * the old build throws on any file carrying it. Global on/off — it is evaluated against whichever ops user decided. */
export const QA_REPORT_PDF_FEATURE_FLAG = "qa-report-pdf";

/** PostHog feature flag: the Jev corrections check, which logs to Axiom and creates no task unless
 * CORRECTIONS_REVIEW_TASK_FEATURE_FLAG is also on. Keep it a global on/off — the check runs as REMI_AUTOMATIONS,
 * so a percentage rollout hashes one bot identity. */
export const CORRECTIONS_JEV_SHADOW_FEATURE_FLAG = "corrections-jev-shadow";

/** PostHog feature flag: a flag from the corrections check creates a Review corrections flag task. Read only while
 * the shadow flag is on, so turning this one off returns to shadow logging. Keep it a global on/off: the check runs
 * as REMI_AUTOMATIONS, so a percentage rollout would hash one bot identity. */
export const CORRECTIONS_REVIEW_TASK_FEATURE_FLAG = "corrections-review-task";

/** PostHog feature flag: a high-confidence crew-trip flag from the corrections check opens the corrections project
 * directly instead of a Review corrections flag task. Read only while the review task flag is on, so turning this one
 * off returns to review tasks only. Keep it a global on/off: the check runs as REMI_AUTOMATIONS. */
export const CORRECTIONS_AUTO_CREATE_FEATURE_FLAG = "corrections-auto-create";

/** PostHog feature flag: contractor daily driver — Today plate plus the Volume, Benefits and supply-console prototypes */
export const CONTRACTOR_DAILY_DRIVER_FEATURE_FLAG = "contractor-daily-driver";

/** PostHog feature flag: the Today page's job-order sections with a deadline column, in place of the SLA-ring stage stack */
export const CONTRACTOR_TODAY_V2_FEATURE_FLAG = "contractor-today-v2";

/** PostHog feature flag: the Today page reads its row deadlines, target cards and drawers from SLA engine clocks instead of the per-card date math */
export const CONTRACTOR_TODAY_SLA_ENGINE_FEATURE_FLAG = "contractor-today-sla-engine";

/** PostHog feature flag: the Today scorecard's A/B/C grade, with the target cards collapsed under it */
export const CONTRACTOR_TODAY_GRADE_FEATURE_FLAG = "contractor-today-grade";

/** PostHog feature flag: Remi users see a contractor's percentile per Today target and their average, on the graded scorecard */
export const CONTRACTOR_TODAY_PERCENTILES_FEATURE_FLAG = "contractor-today-percentiles";

/** PostHog feature flag: Confirm Contractor orders the roster Preferred, then A/B grades, first jobs, C grades, by percentile */
export const CONTRACTOR_ASSIGNMENT_GRADE_ORDER_FEATURE_FLAG = "contractor-assignment-grade-order";

/** PostHog feature flag: Preferred scoring adds a Today-percentile term, and a roof Preferred auto-assign scored but
 * assigned no one is drawn from Pro A, Pro B, new, then Pro C. Turn it on all or nothing: it is evaluated for whoever
 * readied the task, so a percentage rollout would split one market's work between the two assigners by person. */
export const TIERED_CONTRACTOR_AUTO_ASSIGN_FEATURE_FLAG = "tiered-contractor-auto-assign";

/** PostHog feature flag: the Missing SLA targets badge and warning on Preferred contractors ahead of accrual in Confirm Contractor */
export const CONTRACTOR_SLA_WARNING_FEATURE_FLAG = "contractor-sla-warning";

/** PostHog feature flag: text and push a site inspector when a visit on their route is added for today,
 * moved, or removed. */
export const SITE_INSPECTOR_SCHEDULE_NOTIFICATIONS_FEATURE_FLAG = "site-inspector-schedule-notifications";

/** PostHog feature flag: automated homeowner texts for Nomad site inspections and QA visits — the
 * inspector-on-the-way text and the inspection-completed text. */
export const NOMAD_HOMEOWNER_TEXTS_FEATURE_FLAG = "nomad-homeowner-texts";

/** PostHog feature flag: the Quo worksite-contacts menu in the Bid, Project, Task, Ticket and conversation headers.
 * Off keeps the legacy "+" start-conversation button on the Bid Homeowner tab; while PostHog is still
 * resolving the flag neither control renders, so a flagged-in user never sees the "+" flash. */
export const WORKSITE_CONTACTS_MENU_FEATURE_FLAG = "worksite-contacts-menu";

export const SOW_CHANGE_FLOW_FEATURE_FLAG = "sow-change-flow";

/** PostHog feature flag: shows Remi-internal users the training modal announcing the SOW change flow. Each browser
 * sees it once; turn the flag off once the announcement has run its course. */
export const SOW_CHANGE_ANNOUNCEMENT_FEATURE_FLAG = "sow-change-announcement";

/** PostHog feature flag: redesigned checklist experience in the mobile app. Targeted at site inspectors; everyone
 * else stays on the legacy screens. */
export const MOBILE_CHECKLIST_REDESIGN_FEATURE_FLAG = "mobile-checklist-redesign";

/** PostHog feature flag: the mobile app queues every checklist photo on the device and uploads it only
 * through the offline sync, instead of uploading inline while online. */
export const MOBILE_LOCAL_FIRST_PHOTO_CAPTURE_FEATURE_FLAG = "mobile-local-first-photo-capture";

/** PostHog feature flag: the mobile app sends the reads a screen starts together as one batched request. Off sends
 * one request per read. */
export const MOBILE_REQUEST_BATCHING_FEATURE_FLAG = "mobile-request-batching";

export const UNIFIED_SIDEBAR_FEATURE_FLAG = "unified-sidebar";

export const REVIEW_BID_GUIDE_FEATURE_FLAG = "review-bid-guide";

/** PostHog feature flag: Remi acts on the Review Bid guide outcome instead of only showing it. */
export const REVIEW_BID_GUIDE_OUTCOME_ACTIONS_FEATURE_FLAG = "review-bid-guide-outcome-actions";

/** PostHog feature flag: capture Sunrun portal screenshots when Review Bid is created. */
export const REVIEW_BID_PORTAL_CAPTURE_FEATURE_FLAG = "review-bid-portal-capture";

/** PostHog feature flag: contractor work order concession requests (travel and job size adders, scope reports).
 * Read in production only; every other environment, E2E included, treats it as on. Keyed on the requesting contractor. */
export const WORK_ORDER_CONCESSIONS_FEATURE_FLAG = "concessions-work-order";

export const HISTORY_OUTBOX_SEND_FEATURE_FLAG = "history-outbox-send";

/** PostHog feature flag: gates the hourly job that texts contractors and the Job texts settings toggle.
 * Keep it a plain on/off flag: the job evaluates it as REMI_AUTOMATIONS and the toggle per user. */
export const CONTRACTOR_JOB_TEXTS_FEATURE_FLAG = "contractor-job-texts";

/** PostHog feature flag: shows the Push column in notification settings, and with it the job texts Push toggle. Keyed per user. */
export const CONTRACTOR_JOB_TEXTS_PUSH_FEATURE_FLAG = "contractor-job-texts-push";

/** PostHog feature flag: Eden serves real agent-action suggestions to this person in production. Keyed on the person id. */
export const EDEN_AGENT_ACTION_SUGGESTIONS_FEATURE_FLAG = "eden-agent-action-suggestions";

/** PostHog feature flag: kill switch for accepting autoEligible Gray Box suggestions without a person. Off leaves them open. Keyed on the autonomous agent's person id. */
export const AGENT_ACTION_AUTO_ACCEPT_FEATURE_FLAG = "agent-action-auto-accept";

/** PostHog feature flag: runs the nightly Gray Box sweep over every active Schedule work task. Off skips the run. Keyed on the acting automation user. */
export const AGENT_ACTION_SCHEDULE_WORK_SWEEP_FEATURE_FLAG = "agent-action-schedule-work-sweep";

/** PostHog feature flag: Eden shows this person Gray Box's thread-reply and callback drafts. Keyed on the person id.
 * Drafts are generated for every owned thread whether or not its owner has the flag. */
export const THREAD_REPLY_SUGGESTIONS_FEATURE_FLAG = "thread-reply-suggestions";

/** PostHog feature flag: the kill switch for Gray Box thread-reply generation. Off in production until set; always on
 * locally and in preview. Evaluated as the automations bot, so it is all or nothing: never target it by person. */
export const THREAD_REPLY_GENERATION_FEATURE_FLAG = "thread-reply-generation";

export const HISTORY_SLA_BACKFILL_PAUSED_FEATURE_FLAG = "history-sla-backfill-paused";

export const SLA_DEFINITIONS_FEATURE_FLAG = "sla-definitions";

// Keep it global: each caller evaluates it as itself (the scan as REMI_AUTOMATIONS, rescores and manual flags
// as the person), so targeting by person, cohort or percentage makes their tiers disagree and flip-flop.
export const ESCALATION_HOT_GATE_FEATURE_FLAG = "escalation-hot-gate";

export const VIEW_AI_TICKET_COMMENTS_FEATURE_FLAG = "view-ai-ticket-comments";

/** PostHog feature flag: email a training contractor's admin their sandbox invite when provisioning creates or adopts the
 * contractor. Keep it a global on/off — provisioning runs as REMI_AUTOMATIONS, so a percentage rollout evaluates one
 * identity for every contractor. */
export const TRAINING_INVITE_EMAILS_FEATURE_FLAG = "training-invite-emails";

/** PostHog feature flag: a contractor missing a required training module (Remi MSA training) cannot be assigned new
 * work. Keep it a global on/off: auto-assign evaluates it as REMI_AUTOMATIONS and manual assigns as the person, so a
 * targeted rollout would let one path assign a contractor the other refuses. */
export const REQUIRED_TRAINING_BLOCKS_ASSIGNMENT_FEATURE_FLAG = "required-training-blocks-assignment";

/** PostHog feature flag whose JSON payload configures the lead dialer at runtime (`LeadDialerConfigSchema`). Keep it a
 * global 100% rollout: the dial runs from an Inngest function as the automations user, so person targeting never matches.
 * Read fail-closed: off, absent or unparseable means no dial. */
export const LEAD_DIALER_CONFIG_FLAG = "lead-dialer-config";

/** PostHog feature flag whose JSON payload sets each Meta CAPI funnel stage to "shadow" (built and recorded, never
 * sent) or "live". Keep it a global 100% rollout: the relay runs as the automations user. A stage missing from the
 * payload, or an unreadable payload, falls back to that stage's default (`getDefaultStageSendMode`). */
export const META_CAPI_STAGE_MODES_FLAG = "meta-capi-stage-modes";

/** PostHog feature flag: recruiting CRM — Quo webhook ingestion for recruiter lines and the triage queue API. Evaluated for the automations user on webhooks. */
export const RECRUITING_CRM_FEATURE_FLAG = "recruiting-crm";

/** PostHog feature flag: the applicant onboarding hub. */
export const CONTRACTOR_APPLICATION_HUB_FEATURE_FLAG = "contractor-application-hub";

/** PostHog feature flag: the financing tool counts the selected loan plan's dealer fee as a cost on Remi-paper deals
 * and marks a payment selection required on fee-counting, non-insurance deals. Company-wide on/off; read only through
 * `isDealerFeeCostFlagEnabled`. */
export const DEALER_FEE_COST_FEATURE_FLAG = "dealer-fee-cost";
